/**
 * BRRRR Snapshot tab — matches the find-properties.html mockup:
 * - Headline tiles: Cap Rate · NOI (annual) · Rent
 * - ① SHORT-TERM (HARD MONEY) — ACQUISITION
 *   · Cash to Acquire · Monthly Loan Payment
 *   · Holding Costs (wide) with: Hold-months input · Loan Payment (display) ·
 *     Taxes & Insurance (editable) · Utilities (editable) · Monthly Cash Flow
 *     checkbox row · Total Net Holding subtotal
 * - ② LONG-TERM — REFINANCE (AGAINST ARV)
 *   · Monthly Cash Flow · DSCR · Net Cash-Out · Monthly Refi Payment ·
 *     Equity at Refinance (wide) with editable Refi LTV input
 */
import { RefreshCcw } from "lucide-react";

import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { computeBrrrrRefi, fmtMoney, fmtPct, termInMonths } from "@/lib/calculators";
import type { LoanInputs } from "@/types/pipeline";

import { InfoTip } from "../InfoTip";
import type { DealInputsState, DerivedMetrics } from "../detail-helpers";
import { MetricTile } from "./panel-primitives";

interface BrrrrSnapshotProps {
  state: DealInputsState;
  metrics: DerivedMetrics;
  onChange: (patch: Partial<DealInputsState>) => void;
}

const DEFAULT_HOLD_MONTHS = 6;
const DEFAULT_REFI_LTV = 75;

export function BrrrrSnapshot({ state, metrics, onChange }: BrrrrSnapshotProps) {
  const longLoan =
    state.brrrr.long_term ?? ({ ltv: 75, rate: 7, term: 30, points: 1 } as LoanInputs);

  // Short-term acquisition figures come from the derived metrics
  const hmlMonthly = metrics.monthlyDebt;
  const hmlOrigFee = metrics.origFee;
  const hmlLoanAmt = metrics.loanAmount;

  // Editable holding-cost inputs (defaults seeded in buildInitialInputs)
  const holdMonths = state.brrrr.hold_months ?? DEFAULT_HOLD_MONTHS;
  const monthlyTaxes =
    state.brrrr.taxes_monthly ?? Math.round(state.purchase * 0.0018);
  const monthlyUtilities = state.brrrr.utilities_monthly ?? 100;
  const mcfEnabled = state.brrrr.mcf_enabled ?? false;
  const mcfDelay = Math.max(0, Math.min(state.brrrr.mcf_delay ?? 0, holdMonths));

  // Monthly cash flow during the hold (NOI − HML debt). Negative = cost.
  const cfNetShort = metrics.cashflow.noi - hmlMonthly;
  const sumMonthly = hmlMonthly + monthlyTaxes + monthlyUtilities;
  const sumMonths = mcfEnabled ? mcfDelay : holdMonths;
  const mcfMonths = mcfEnabled ? Math.max(0, holdMonths - mcfDelay) : 0;
  // mcfMonthly is the positive cost when cfNetShort is negative
  const mcfMonthly = -cfNetShort;
  const totalHolding = sumMonthly * sumMonths + mcfMonthly * mcfMonths;
  // What the MCF strikethrough chip shows when MCF is disabled
  const wouldBeMcfTotal = cfNetShort * holdMonths;

  // Refinance LTV is editable on this panel — drives Step 2 math. It lives on
  // the long-term loan's ltv (against ARV).
  const refiLtv = longLoan.ltv ?? DEFAULT_REFI_LTV;

  const refi = computeBrrrrRefi({
    arv: state.arv,
    refiLtvPct: refiLtv,
    refiRatePct: longLoan.rate,
    refiTermYears: termInMonths(longLoan, "years") / 12,
    refiPoints: longLoan.points,
    hmlLoanAmt,
    hmlMonthlyInterest: hmlMonthly,
    holdMonths,
    totalInDeal: state.purchase + state.rehab + metrics.closingDollar + hmlOrigFee,
  });
  const refiCashflow = metrics.cashflow.noi - refi.refiEmi;
  const refiDscr = refi.refiEmi > 0 ? metrics.cashflow.noi / refi.refiEmi : 0;

  // Cash to acquire = Purchase + Rehab + Closing + Origination − Loan
  const cashToAcquire =
    state.purchase + state.rehab + metrics.closingDollar + hmlOrigFee - hmlLoanAmt;

  function patchBrrrr(patch: Partial<DealInputsState["brrrr"]>) {
    onChange({ brrrr: { ...state.brrrr, ...patch } });
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <RefreshCcw className="h-4 w-4 text-primary" aria-hidden />
        BRRRR Snapshot
        <InfoTip text="Buy · Rehab · Rent · Refinance · Repeat — the key metrics that matter for the refinance step." />
      </h3>

      {/* Headline tiles */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricTile
          label="Cap Rate"
          value={fmtPct(metrics.capRatePct)}
          sub="NOI ÷ Price"
          tone={metrics.capRatePct >= 0 ? "pos" : "neg"}
        />
        <MetricTile
          label="NOI (annual)"
          value={fmtMoney(metrics.cashflow.noi * 12)}
          sub="Net Operating Income"
        />
        <MetricTile
          label="Rent"
          value={fmtMoney(metrics.monthlyRent)}
          sub="Monthly"
        />
      </div>

      {/* ── Short-term acquisition ── */}
      <NumberedSectionHeader number={1}>
        Short-Term (Hard Money) — Acquisition
      </NumberedSectionHeader>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricTile
          label="Cash to Acquire"
          value={fmtMoney(cashToAcquire)}
          sub="Purchase + Rehab + Closing − HML"
        />
        <MetricTile
          label="Monthly Loan Payment"
          value={fmtMoney(hmlMonthly)}
          sub="Amortizing EMI"
        />

        {/* Holding costs — wide tile with editable breakdown */}
        <div className="rounded-md border border-border bg-muted/40 p-3 sm:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">Holding Costs</div>
            <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <span>Hold</span>
              <NumberInput
                min={1}
                max={36}
                value={holdMonths}
                parse={(raw) => Math.max(1, Math.min(36, Number(raw) || 1))}
                onChange={(v) => patchBrrrr({ hold_months: v })}
                className="h-6 w-14 border-border bg-card px-2 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="Hold months"
              />
              <span>mo</span>
            </div>
          </div>
          <div
            className={cn(
              "mt-1 text-lg font-extrabold leading-tight",
              totalHolding > 0 ? "text-destructive" : "text-success",
            )}
          >
            {totalHolding < 0 ? "+" : "−"}
            {fmtMoney(Math.abs(totalHolding))}
          </div>

          <div className="mt-3 flex flex-col divide-y divide-dashed divide-border/60">
            {/* Loan Payment — display only, from financing */}
            <HoldDisplayRow
              label="Loan Payment"
              sub="from Financing"
              value={-hmlMonthly}
              dim={mcfEnabled && mcfDelay === 0}
            />

            {/* Taxes & Insurance — editable */}
            <HoldEditableRow
              label="Taxes & Insurance"
              value={monthlyTaxes}
              onChange={(v) => patchBrrrr({ taxes_monthly: v })}
              dim={mcfEnabled && mcfDelay === 0}
            />

            {/* Utilities — editable */}
            <HoldEditableRow
              label="Utilities"
              value={monthlyUtilities}
              onChange={(v) => patchBrrrr({ utilities_monthly: v })}
              dim={mcfEnabled && mcfDelay === 0}
            />

            {/* Monthly Cash Flow checkbox */}
            <div className="flex items-center justify-between gap-2 py-2 text-[12px]">
              <label className="inline-flex cursor-pointer items-center gap-2 text-muted-foreground">
                <input
                  type="checkbox"
                  checked={mcfEnabled}
                  onChange={(e) =>
                    patchBrrrr({ mcf_enabled: e.target.checked })
                  }
                  className="h-3.5 w-3.5 cursor-pointer accent-primary"
                />
                <span>Monthly Cash Flow</span>
                {mcfEnabled && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/70">
                    starts after
                    <NumberInput
                      min={0}
                      max={holdMonths}
                      value={mcfDelay}
                      parse={(raw) =>
                        Math.max(0, Math.min(holdMonths, Number(raw) || 0))
                      }
                      onChange={(v) => patchBrrrr({ mcf_delay: v })}
                      className="h-5 w-12 border-border bg-card px-1.5 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                      aria-label="MCF delay months"
                    />
                    mo
                  </span>
                )}
              </label>
              {mcfEnabled ? (
                <span
                  className={cn(
                    "font-semibold",
                    cfNetShort < 0 ? "text-destructive" : "text-success",
                  )}
                >
                  {cfNetShort < 0 ? "−" : "+"}
                  {fmtMoney(Math.abs(cfNetShort * mcfMonths))}
                  {mcfDelay > 0 && (
                    <span className="ml-1 text-[10px] font-medium text-muted-foreground">
                      ({mcfMonths} mo × {fmtMoney(Math.abs(cfNetShort))})
                    </span>
                  )}
                </span>
              ) : (
                <span className="text-muted-foreground/70 line-through">
                  {wouldBeMcfTotal < 0 ? "−" : "+"}
                  {fmtMoney(Math.abs(wouldBeMcfTotal))}
                </span>
              )}
            </div>

            {/* Total subtotal — bold, separated by top border */}
            <div className="flex items-center justify-between border-t border-border pt-2 text-[12px] font-bold">
              <span>
                Total Net Holding{" "}
                <span className="text-[10px] font-medium text-muted-foreground">
                  over {holdMonths} mo
                </span>
              </span>
              <span
                className={cn(
                  totalHolding > 0 ? "text-destructive" : "text-success",
                )}
              >
                {totalHolding < 0 ? "+" : "−"}
                {fmtMoney(Math.abs(totalHolding))}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Long-term refinance ── */}
      <NumberedSectionHeader number={2}>
        Long-Term — Refinance (against ARV)
      </NumberedSectionHeader>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricTile
          label="Monthly Cash Flow"
          value={
            (refiCashflow < 0 ? "−" : "") + fmtMoney(Math.abs(refiCashflow))
          }
          sub="After refi · long-term debt"
          tone={refiCashflow >= 0 ? "pos" : "neg"}
        />
        <MetricTile
          label="DSCR"
          value={refiDscr.toFixed(2)}
          sub="NOI ÷ Refi payment"
          tone={refiDscr >= 1.2 ? "pos" : refiDscr >= 1 ? "dim" : "neg"}
        />
        <MetricTile
          label="Net Cash-Out Amount"
          value={
            (refi.netCashOut < 0 ? "−" : "") + fmtMoney(Math.abs(refi.netCashOut))
          }
          sub="Refi loan − Step 1 loan − fees − holding"
          tone={refi.netCashOut >= 0 ? "pos" : "neg"}
        />
        <MetricTile
          label="Monthly Refi Payment"
          value={fmtMoney(refi.refiEmi)}
          sub="Amortized EMI"
        />

        {/* Equity at Refinance — wide tile with editable Refi LTV */}
        <div className="rounded-md border border-border bg-muted/40 p-3 sm:col-span-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">
              Equity at Refinance
            </div>
            <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <span>Refi LTV</span>
              <NumberInput
                min={50}
                max={100}
                value={refiLtv}
                parse={(raw) => Math.max(50, Math.min(100, Number(raw) || refiLtv))}
                onChange={(v) => patchBrrrr({ long_term: { ...longLoan, ltv: v } })}
                className="h-6 w-14 border-border bg-card px-2 py-0 text-right text-[11px] font-bold focus-visible:ring-0 focus-visible:ring-offset-0"
                aria-label="Refinance LTV percent"
              />
              <span>%</span>
            </div>
          </div>
          <div
            className={cn(
              "mt-1 text-lg font-extrabold leading-tight",
              refi.equityAtRefi >= 0 ? "text-success" : "text-destructive",
            )}
          >
            {fmtMoney(refi.equityAtRefi)}
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">
            ARV − Refi loan · Cash left in:{" "}
            {refi.cashLeftIn >= 0 ? "" : "−"}
            {fmtMoney(Math.abs(refi.cashLeftIn))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Section header with a circled number (① / ②) — matches the mockup style.
 * ────────────────────────────────────────────────────────────────────────── */

function NumberedSectionHeader({
  number,
  children,
}: {
  number: number;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-5 mb-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-primary">
      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-primary text-[10px] font-bold">
        {number}
      </span>
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Holding-cost row primitives
 * ────────────────────────────────────────────────────────────────────────── */

interface HoldDisplayRowProps {
  label: string;
  sub?: string;
  value: number;
  dim?: boolean;
}

function HoldDisplayRow({ label, sub, value, dim }: HoldDisplayRowProps) {
  const isNegative = value < 0;
  return (
    <div
      className={cn(
        "flex items-center justify-between py-2 text-[12px] transition-opacity",
        dim && "opacity-40",
      )}
    >
      <span className="text-muted-foreground">
        {label}
        {sub && (
          <span className="ml-1 text-[10px] font-medium text-muted-foreground/70">
            {sub}
          </span>
        )}
      </span>
      <span
        className={cn(
          "font-semibold",
          isNegative ? "text-destructive" : "text-foreground",
        )}
      >
        {isNegative ? "−" : ""}
        {fmtMoney(Math.abs(value))}
      </span>
    </div>
  );
}

interface HoldEditableRowProps {
  label: string;
  /** Positive cost value — rendered as "-$X" inside a red-tinted input. */
  value: number;
  onChange: (v: number) => void;
  dim?: boolean;
}

function HoldEditableRow({
  label,
  value,
  onChange,
  dim,
}: HoldEditableRowProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 py-2 text-[12px] transition-opacity",
        dim && "opacity-40",
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <div className="relative w-28 rounded-md border border-destructive/30 bg-card focus-within:border-destructive">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[12px] font-semibold text-destructive">
          -$
        </span>
        <NumberInput
          min={0}
          value={value}
          parse={(raw) => Math.max(0, Number(raw) || 0)}
          onChange={onChange}
          className="h-7 w-full border-0 bg-transparent pl-8 pr-2 text-right text-[12px] font-bold text-destructive focus-visible:ring-0 focus-visible:ring-offset-0"
          aria-label={label}
        />
      </div>
    </div>
  );
}
